import api from 'config/axiosConfig';
import { parseWikipediaData } from 'util/wikiUtils';
import { Issue } from 'util/constants';
import { WikiSuggestion, Suggestion } from 'hooks/useIssueSearch';
import { apiRoutes } from "util/apiRoutes";

/**
 * Upload an image for an issue from a URL
 * 
 * @param imageUrl - The URL of the image to upload
 * @param issueId - The ID of the issue to attach the image to
 * @returns The uploaded image URL, or null if upload failed
 */
export const uploadIssueImage = async (imageUrl: string, issueId: number): Promise<string | null> => {
  if (!imageUrl) {
    return null;
  }

  try {
    const imageResponse = await fetch(imageUrl);

    let contentType = imageResponse.headers.get('content-type');

    if (!contentType) {
      contentType = 'image/jpeg';
    }
    if (contentType.includes('image/svg+xml')) {
      contentType = 'image/svg';
    }
    if (contentType.includes('image/jpeg')) {
      contentType = 'image/jpg';
    }

    const blob = await imageResponse.blob();

    if (blob) {
      const formData = new FormData();
      formData.append('image', blob, `reference_image.${contentType?.split('/')[1].replace('jpeg', 'jpg')}`);
      const response = await api.post(apiRoutes.issues.image(issueId), formData);
      return response.data.url;
    }

    return null;
  } catch (err) {
    console.error('Error uploading reference image:', err);
    return null;
  }
};

/**
 * Upload an image for an issue from a blob
 * 
 * @param issueId - The ID of the issue to attach the image to
 * @param blob - The blob to upload
 * @param filename - The filename of the image
 * @returns The uploaded image URL, or null if upload failed
 */
export async function uploadIssueImageFromBlob(issueId: number, blob: Blob, filename = "reference_image.jpg") {
  const fd = new FormData();
  fd.append("image", blob, filename);
  await api.post(apiRoutes.issues.image(issueId), fd);
} 

/**
 * Create an issue from a Wikipedia suggestion
 * 
 * @param wikiSuggestion - The Wikipedia suggestion to create an issue from
 * @returns The created issue
 */
export const createIssueFromWikipedia = async (wikiSuggestion: WikiSuggestion): Promise<Issue> => {
  try {
    // Parse Wikipedia data to extract additional information including images
    const wikiImg = await parseWikipediaData(wikiSuggestion.cname, true);

    // Create the issue
    const response = await api.post(apiRoutes.issues.root, {
      name: wikiSuggestion.name,
      cname: wikiSuggestion.cname,
      subject_cname: wikiSuggestion.cname,
      wikipedia_link: wikiSuggestion.link,
      subject_properties: {
        ...wikiSuggestion.subject_properties,
        wiki_parse_first_img: wikiImg.wiki_parse_first_img
      }
    });

    const createdIssue: Issue = response.data;

    // Upload the issue image if available
    if (createdIssue.properties?.subject_properties?.wiki_parse_first_img) {
      await uploadIssueImage(
        createdIssue.properties.subject_properties.wiki_parse_first_img,
        createdIssue.id
      );
    }

    return createdIssue;
  } catch (error) {
    console.error('Error creating issue from Wikipedia:', error);
    throw error;
  }
};

export interface CustomIssuePayload {
  title: string;
  cname: string;
  type: string;
  description: string;
  referenceLink: string | null;
  defaultThumbnail: string;
}

/**
 * Create a custom (non-Wikipedia) issue.
 *
 * @param payload - Custom issue fields.
 * @returns The created issue.
 */
export const createCustomIssue = async (payload: CustomIssuePayload): Promise<Issue> => {
  const { title, cname, type, description, referenceLink, defaultThumbnail } = payload;

  const response = await api.post(apiRoutes.issues.root, {
    name: title,
    subject_cname: cname,
    type,
    description,
    reference_link: referenceLink,
    subject_properties: {
      is_custom_issue: true,
      thumbnail: {
        source: defaultThumbnail,
      },
      extract: description,
    },
  });

  return response.data as Issue;
};

/**
 * Ensure an issue exists from a suggestion (creating it if it's a Wikipedia suggestion)
 * 
 * @param suggestion - The suggestion (either existing issue or Wikipedia)
 * @returns The issue (either the existing one or newly created)
 */
export const ensureIssueExists = async (suggestion: Suggestion): Promise<Issue> => {
  if (suggestion.type === 'issue') {
    // Already an existing issue, just return it
    return suggestion as unknown as Issue;
  } else {
    // Wikipedia suggestion, create the issue
    return await createIssueFromWikipedia(suggestion);
  }
};

/**
 * Create a compound issue (influence relationship) between two issues
 * 
 * @param subjectCname - The cname of the influencer issue
 * @param topicCname - The cname of the influenced issue
 * @param subjectWikipediaLink - Wikipedia link for the subject
 * @param topicWikipediaLink - Wikipedia link for the topic
 * @param subjectProperties - Properties for the subject
 * @param topicProperties - Properties for the topic
 * @returns The created compound issue
 */
export const createCompoundIssue = async (
  subjectCname: string,
  topicCname: string,
  subjectWikipediaLink: string | null,
  topicWikipediaLink: string | null,
  subjectProperties?: Record<string, unknown>,
  topicProperties?: Record<string, unknown>
): Promise<Issue> => {
  try {
    const response = await api.post(apiRoutes.issues.root, {
      subject_cname: subjectCname,
      topic_cname: topicCname,
      wikipedia_link: subjectWikipediaLink,
      topic_wikipedia_link: topicWikipediaLink,
      subject_properties: subjectProperties,
      topic_properties: topicProperties
    });

    return response.data;
  } catch (error) {
    console.error('Error creating compound issue:', error);
    throw error;
  }
};

